import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { COMPANY } from "@/config/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSession = () => {
  let s = localStorage.getItem("ayx_chat_session");
  if (!s) {
    s = (crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("ayx_chat_session", s);
  }
  return s;
};

const GREETING = {
  role: "assistant",
  content:
    "Hello! I'm the Aadiyogis trade assistant. Ask me anything about our grains, spices, oilseeds or fresh produce — pricing, shipping terms or payment options. How can I help?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "" });
  const sessionId = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, showLead]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`, {
        session_id: sessionId.current, message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Sorry, I'm having trouble right now. Please email " + COMPANY.email + " and our team will assist you.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) {
      toast.error("Please add your name and email.");
      return;
    }
    try {
      await axios.post(`${API}/chat/lead`, { session_id: sessionId.current, ...lead });
      setLeadSent(true);
      setShowLead(false);
      setMessages((m) => [...m, {
        role: "assistant",
        content: `Thank you, ${lead.name}. Our trade desk has your details and will reach out shortly. Meanwhile, feel free to keep asking questions.`,
      }]);
      toast.success("Details shared with our trade desk.");
    } catch (e) {
      toast.error("Could not save your details. Please try again.");
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-[#1A3626] text-[#E6C97A] shadow-2xl flex items-center justify-center border border-[#C5A059]/40"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Open chat assistant"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} strokeWidth={1.5} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageCircle size={24} strokeWidth={1.5} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] max-w-[380px] h-[560px] max-h-[calc(100vh-8rem)] bg-white shadow-2xl border border-[#1A3626]/15 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#1A3626] px-5 py-4 relative">
              <div className="absolute inset-0 grain"></div>
              <div className="relative flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#C5A059] text-[#1A3626] flex items-center justify-center font-serif-display text-lg">A</div>
                <div>
                  <div className="text-white font-medium text-sm">Trade Assistant</div>
                  <div className="text-[#E6C97A] text-xs flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-400"></span> Online · 24/7
                  </div>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F9F8F6]" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1A3626] text-white"
                        : "bg-white border border-[#1A3626]/10 text-[#1F2937]"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#1A3626]/10 px-4 py-3 flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="h-2 w-2 rounded-full bg-[#C5A059]"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {showLead && (
                <form onSubmit={submitLead} className="bg-white border border-[#C5A059]/40 p-4 space-y-2" data-testid="chat-lead-form">
                  <div className="text-xs uppercase tracking-widest text-[#1A3626]/60">Share your contact</div>
                  <Input data-testid="chat-lead-name" placeholder="Full name *" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="rounded-none border-[#1A3626]/20 h-9 text-sm" />
                  <Input data-testid="chat-lead-email" type="email" placeholder="Email *" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="rounded-none border-[#1A3626]/20 h-9 text-sm" />
                  <Input data-testid="chat-lead-phone" placeholder="Phone / WhatsApp" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="rounded-none border-[#1A3626]/20 h-9 text-sm" />
                  <button type="submit" data-testid="chat-lead-submit" className="btn-gold w-full justify-center !py-2 text-sm">Send to trade desk</button>
                </form>
              )}
            </div>

            {/* Footer actions */}
            <div className="border-t border-[#1A3626]/10 bg-white">
              {!leadSent && !showLead && (
                <button
                  data-testid="chat-share-contact"
                  onClick={() => setShowLead(true)}
                  className="w-full text-xs text-[#1A3626] hover:bg-[#F9F8F6] py-2 flex items-center justify-center gap-1.5 border-b border-[#1A3626]/5"
                >
                  <User size={13} strokeWidth={1.5} className="text-[#C5A059]" /> Share contact for a follow-up
                </button>
              )}
              <div className="p-3 flex items-center gap-2">
                <Input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
                  placeholder="Ask about products, pricing, shipping..."
                  className="rounded-none border-[#1A3626]/20 h-10 text-sm focus-visible:ring-[#C5A059]"
                />
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={loading}
                  className="h-10 w-10 shrink-0 bg-[#1A3626] text-[#E6C97A] flex items-center justify-center hover:bg-[#244A35] disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Send size={16} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
